import * as React from 'react';
import propz from 'propz';
import { Field, Form, Formik } from 'formik';
import { HTMLEditorQuillForNews } from 'Src/components/HTMLEditorQuill/HTMLEditorQuillForNews';
import * as Yup from 'yup';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import { AppUser } from 'Src/views/App/App';
import { LeagueNews, LeagueNewsAttachment } from 'Src/models/schoolLeague';
import moment from 'moment';
import { uploadFile } from 'Src/helpers/service/file';
import { uploadImage } from 'Src/helpers/service/image';
import 'Src/components/NewsForm/NewsForm.css';
import './SchoolLeagueNewsForm.scss';

interface Props {
  onCloseClick: () => void;
  onFormSubmit: (data) => void;
  user: AppUser;
  news?: LeagueNews;
}
interface State {
  isEditorActive: boolean;
  isLoadingFile: boolean;
  isLoadingImage: boolean;
  files: LeagueNewsAttachment[];
  images: LeagueNewsAttachment[];
}

export class SchoolLeagueNewsForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const { news } = props;

    this.state = {
      isEditorActive: false,
      isLoadingFile: false,
      isLoadingImage: false,
      files: propz.get(news, ['attachments', 'files'], []),
      images: propz.get(news, ['attachments', 'images'], [])
    };
  }

  onShowEditorClick = () => {
    const { isEditorActive } = this.state;
    this.setState({
      isEditorActive: !isEditorActive
    });
  };

  onFileChange = (event): void => {
    const { user } = this.props;

    const file = event.target.files[0];
    const fileName = file.name;
    const fileNameWithoutSpaces = fileName.split(' ').join('_');
    const formData = new FormData();
    formData.append('file', file, fileNameWithoutSpaces);

    this.setState({
      isLoadingFile: true
    });

    uploadFile(user, formData).then(data => {
      const { key } = data;

      const filesNext = [
        ...this.state.files,
        {
          url: key,
          name: fileNameWithoutSpaces
        }
      ];

      this.setState({
        isLoadingFile: false,
        files: filesNext
      });
    });
  };

  onImageChange = event => {
    const { user } = this.props;
    const image = event.target.files[0];
    const imageName = image.name;

    this.setState({
      isLoadingImage: true
    });

    uploadImage(user, image).then(response => {
      const picUrl = `${window.apiImg}/images/${response.key}`;

      const imagesNext = [
        ...this.state.images,
        {
          url: picUrl,
          name: imageName
        }
      ];

      this.setState({
        images: imagesNext,
        isLoadingImage: false
      });
    });
  };

  onSubmit = values => {
    const { onFormSubmit } = this.props;
    const { files, images } = this.state;

    const dataToPost = {
      ...values,
      attachments: {
        files,
        images
      }
    };

    onFormSubmit(dataToPost);
  };

  onRemoveFilesClick = fileIndex => {
    const { files } = this.state;
    const filesNext = files.filter((file, index) => fileIndex !== index);
    this.setState({
      files: filesNext
    });
  };

  renderFiles() {
    const { files } = this.state;

    return files.map((file, index) => {
      return (
        <div key={`files_${index}`} className="mMaxWidth200px">
          <button type="button" className="close" onClick={() => this.onRemoveFilesClick(index)}>
            <span>&times;</span>
          </button>
          <div>{file.name}</div>
        </div>
      );
    });
  }

  onRemoveImageClick = imageIndex => {
    const { images } = this.state;
    const imagesNext = images.filter((image, index) => imageIndex !== index);
    this.setState({
      images: imagesNext
    });
  };

  renderImages() {
    const { images } = this.state;

    return images.map((image, index) => {
      return (
        <div key={`images_${index}`} className="mMaxWidth200px">
          <button type="button" className="close" onClick={() => this.onRemoveImageClick(index)}>
            <span>&times;</span>
          </button>
          <img className="img-fluid img-thumbnail" src={image.url} alt="league news image" />
        </div>
      );
    });
  }

  render() {
    const { onCloseClick, news } = this.props;
    const { isLoadingFile, isLoadingImage } = this.state;

    const newsDate = propz.get(news, ['date']);
    const date = moment(newsDate, 'DD-MM-YYYY HH:mm aa').isValid() ? new Date(newsDate) : new Date();

    const newsInitial = {
      date: date,
      title: propz.get(news, ['title'], ''),
      body: propz.get(news, ['body'], '')
    };

    const newsSchema = Yup.object().shape({
      title: Yup.string().required('Required'),
      body: Yup.string().required('Required')
    });

    return (
      <div className="newsEditor leagueNewsEditor">
        <div className="row">
          <div className="col-md-12">
            <Formik
              initialValues={newsInitial}
              validationSchema={newsSchema}
              onSubmit={values => this.onSubmit(values)}
            >
              {({ touched, errors, setFieldValue, values }) => (
                <Form className="leagueNewsEditor__form">
                  <div className="container-fluid">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="form-group">
                          <label>Title</label>
                          <Field type="text" name="title" className="form-control" placeholder="Enter news title" />
                        </div>
                        {touched.title && errors.title ? (
                          <div className="alert alert-danger">{errors.title}</div>
                        ) : null}

                        <div className="form-group">
                          <label>Content</label>
                          <div className="eNewsTextEditor newsEditor__richText">
                            <HTMLEditorQuillForNews
                              user={this.props.user}
                              onTextChange={body => {
                                setFieldValue('body', body);
                              }}
                              value={values.body}
                              isEnabled={true}
                              placeholder="Enter news content"
                            />
                          </div>
                        </div>
                        {touched.body && errors.body ? <div className="alert alert-danger">{errors.body}</div> : null}

                        <div className="form-group">
                          <SitDatePicker
                            label="Publish date and time"
                            mode="datetime"
                            value={values.date}
                            onChange={date => setFieldValue('date', date)}
                            format="DD MMM YYYY, HH:mm"
                            minuteStep={5}
                            required={true}
                            clearable={false}
                            className="newsEditor__datePicker"
                          />
                        </div>

                        {isLoadingFile ? (
                          <div>Loading...</div>
                        ) : (
                          <div className="form-group">
                            <label htmlFor="files">Files</label>
                            <input type="file" className="newsEditor__file" id="files" onChange={this.onFileChange} />
                          </div>
                        )}

                        <div className="form-group">{this.renderFiles()}</div>

                        {isLoadingImage ? (
                          <div>Loading...</div>
                        ) : (
                          <div className="form-group">
                            <label htmlFor="images">Images</label>
                            <input type="file" className="newsEditor__file" id="images" onChange={this.onImageChange} />
                          </div>
                        )}

                        <div className="form-group">{this.renderImages()}</div>

                        <div className="newsEditor__actions">
                          <button type="button" className="newsEditor__cancel" onClick={onCloseClick}>
                            Cancel
                          </button>
                          <button type="submit" className="newsEditor__save">
                            Save news
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    );
  }
}
